import { safePath, setReturnTo, takeReturnTo } from "../lib/returnTo";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config";

// Email + OTP only (Google sign-in removed — matches the analystbro-excel plugin).
// In dev, the backend returns dev_code (DEBUG only) and we prefill it so sign-in is one click.
// Connector sign-in (ChatGPT/Claude/Gemini): open the server session here with the token we
// just received, instead of relying on the bridge page reading localStorage. In the iPhone
// sign-in sheet the bridge found no token and sent users back to the login page in a loop.
async function finishConnectorSignIn(access, serverNext) {
  try {
    const r = await fetch("/api/mcp/web/session/", {
      method: "POST",
      headers: { Authorization: `Bearer ${access}` },
      credentials: "same-origin",
    });
    const inner = new URLSearchParams(serverNext.split("?")[1] || "").get("next") || "";
    if (r.ok && inner.startsWith("/o/authorize/")) {
      window.location.replace(inner);
      return;
    }
  } catch (e) {
    // fall back to the bridge page
  }
  window.location.replace(serverNext);
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam0 = new URLSearchParams(location.search).get("next") || "";
  const from = safePath(location.state?.from?.pathname) || safePath(nextParam0) || "/agentbro";
  // Remember it across full page loads (and for PublicRoute, which redirects as soon as we're signed in).
  useEffect(() => {
    if (from !== "/agentbro") setReturnTo(from);
  }, [from]);
  // Web MCP (plan 282): an AI app's sign-in sends people here with ?next= pointing back at the
  // approval step on our server. Only that server path is honoured, and it needs a full page load.
  const nextParam = new URLSearchParams(location.search).get("next") || "";
  const serverNext = nextParam.startsWith("/api/mcp/web/login/") ? nextParam : "";
  const { setSession, isAuthenticated } = useAuth();

  const [errors, setErrors] = useState({});
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [info, setInfo] = useState("");
  const signingInRef = useRef(false);
  const [devCode, setDevCode] = useState(null); // set only in dev (DEBUG backend)

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !signingInRef.current) {
      if (serverNext) window.location.replace(serverNext);
      else navigate(takeReturnTo(from), { replace: true });
    }
  }, [isAuthenticated, navigate, from, serverNext]);

  const handleSendCode = async (e) => {
    if (e) e.preventDefault();
    setErrors({});
    setInfo("");
    setDevCode(null);

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: "Please enter a valid email address." });
      return;
    }

    setSending(true);
    try {
      const resp = await axios.post(`${API_BASE_URL}/api/auth/otp/request`, { email });
      setCodeSent(true);
      const dc = resp.data?.dev_code;
      if (dc) {
        // Local dev (DEBUG): prefill the code so sign-in is one click.
        setDevCode(dc);
        setCode(dc);
        setInfo("Dev mode — code auto-filled.");
      } else {
        setInfo(`We sent a 6-digit code to ${email}.`);
      }
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        setErrors({
          general:
            "You requested a code too recently. Please wait a moment and try again.",
        });
      } else {
        setErrors({
          general:
            error.response?.data?.error ||
            error.response?.data?.detail ||
            "Couldn't send the code. Please try again.",
        });
      }
    } finally {
      setSending(false);
    }
  };

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();
    setErrors({});

    if (!code || code.trim().length !== 6) {
      setErrors({ code: "Enter the 6-digit code from your email." });
      return;
    }

    setVerifying(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/otp/verify`, {
        email,
        code: code.trim(),
      });
      const { access, refresh, user: userData } = response.data;
      signingInRef.current = true;
      setSession(access, { refresh, user: userData });

      if (typeof gtag !== "undefined") {
        gtag("event", "login", {
          method: "email_otp",
          event_category: "authentication",
          event_label: "success",
        });
      }

      if (serverNext) await finishConnectorSignIn(access, serverNext);
      else navigate(takeReturnTo(from), { replace: true });
    } catch (error) {
      signingInRef.current = false;
      const status = error.response?.status;
      if (status === 429) {
        setErrors({
          general: "Too many attempts. Please request a new code and try again.",
        });
      } else if (status === 400) {
        setErrors({ code: "That code is invalid or expired. Please try again." });
      } else {
        setErrors({
          general:
            error.response?.data?.error ||
            error.response?.data?.detail ||
            "Verification failed. Please try again.",
        });
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 relative flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute w-[600px] h-[600px] bg-gradient-to-br from-brand-cta/8 via-emerald-500/5 to-transparent rounded-full blur-3xl -top-48 -right-48 animate-float"></div>
        <div className="absolute w-[500px] h-[500px] bg-gradient-to-tl from-brand-cta/6 via-teal-500/4 to-transparent rounded-full blur-3xl top-1/2 -left-32 animate-float-delayed"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo and Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            {/* Same mark + wordmark as the navbar.  */}
            <div className="flex items-center gap-2">
              <img src="/images/analystbro-logo.png" alt="" className="h-10 w-10" />
              <span className="font-display font-bold text-2xl tracking-tight text-brand-dark">AnalystBro</span>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-brand-dark mb-2">
            Sign in to your account
          </h2>
          <p className="text-gray-600">We'll email you a one-time code — no password needed.</p>
        </div>

        {/* Form Card */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
          {errors.general && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}
          {info && !errors.general && (
            <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-md text-sm">
              {info}
            </div>
          )}

          <div className="space-y-6">
            {/* Email OTP */}
            {!codeSent ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-brand-cta focus:ring-1 focus:ring-brand-cta outline-none"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full rounded-lg bg-brand-dark px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {sending ? "Sending code..." : "Send sign-in code"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    6-digit code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="123456"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm tracking-[0.4em] text-center focus:border-brand-cta focus:ring-1 focus:ring-brand-cta outline-none"
                  />
                  {devCode && (
                    <p className="mt-1 text-center text-xs text-gray-500">
                      dev code: <strong>{devCode}</strong>
                    </p>
                  )}
                  {errors.code && (
                    <p className="mt-1 text-sm text-red-600">{errors.code}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={verifying}
                  className="w-full rounded-lg bg-brand-dark px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {verifying ? "Verifying..." : "Verify & sign in"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCodeSent(false);
                    setCode("");
                    setDevCode(null);
                    setErrors({});
                    setInfo("");
                  }}
                  className="w-full text-center text-sm text-gray-500 hover:text-gray-800 transition-colors"
                >
                  Use a different email
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
